import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  IconButton,
  Grid,
  CircularProgress,
  Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { formatDate } from '../../../utils/DateUtils';
import EmailListService from '../../../services/EmailListService';
import dayjs from 'dayjs';

const ViewListModal = ({ open, onClose, list }) => {
  const { t } = useTranslation();
  const [listData, setListData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);

  // Cargar los datos completos de la lista cuando se abre el modal
  useEffect(() => {
    const loadListData = async () => {
      if (open && list && list.id) {
        try {
          setLoading(true);
          console.log('[ViewListModal] Loading list data:', list.id);
          
          const fullListData = await EmailListService.getListById(list.id);
          
          console.log('[ViewListModal] List data loaded:', {
            id: fullListData?.id,
            name: fullListData?.name,
            contactsCount: fullListData?.contacts?.length || 0
          });
          
          setListData(fullListData);
          setContacts(fullListData?.contacts || []);
        } catch (error) {
          console.error('[ViewListModal] Error loading list data:', error);
          // Si falla, usar los datos básicos que ya tenemos
          setListData(list);
          setContacts([]);
        } finally {
          setLoading(false);
        }
      } else if (open && list) {
        // Si no hay ID pero hay datos básicos, usarlos
        setListData(list);
        setContacts([]);
      }
    };
    
    loadListData();
  }, [open, list]);

  // Limpiar cuando se cierra el modal
  useEffect(() => {
    if (!open) {
      setListData(null);
      setContacts([]);
    }
  }, [open]);

  if (!list) return null;
  
  const displayList = listData || list;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getIcon('eye')}
          {t('view_list') || 'Ver Lista'}
        </Box>
        <IconButton 
          onClick={onClose} 
          size="small"
          sx={{
            width: 40,
            height: 40,
            minWidth: 40,
            minHeight: 40,
            maxWidth: 40,
            maxHeight: 40,
            padding: 0,
            borderRadius: '50%',
            '&:hover': {
              borderRadius: '50%'
            }
          }}
        >
          {getIcon('close')}
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('name')}
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                {displayList.name}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('description')}
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                {displayList.description || t('no_description') || 'Sin descripción'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={4}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('contacts')}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip label={contacts.length || displayList.contacts || 0} size="medium" color="info" />
              </Box>
            </Grid>

            <Grid item xs={4}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('created')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {displayList.created ? formatDate(dayjs(displayList.created)) : '-'}
              </Typography>
            </Grid>

            <Grid item xs={4}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('last_used')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {displayList.lastUsed ? formatDate(dayjs(displayList.lastUsed)) : '-'}
              </Typography>
            </Grid>

            {/* Sección de clientes */}
            {contacts.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('clients') || 'Clientes'} ({contacts.length})
                  </Typography>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      maxHeight: 300, 
                      overflowY: 'auto',
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {contacts.map((contact, index) => (
                        <Chip
                          key={contact.id || index}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2">
                                {contact.name || contact.email || `Cliente ${contact.id || index + 1}`}
                              </Typography>
                              {contact.email && (
                                <Typography variant="caption" color="text.secondary">
                                  ({contact.email})
                                </Typography>
                              )}
                            </Box>
                          }
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              </>
            )}
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewListModal;

